import { Link } from "react-router-dom"
import { useState, useEffect } from "react"
import { TaskStatusIcon } from "./TaskChangeIcon"
import { TaskTypeIcon } from "./TaskTypeIcon"
import InfiniteScroll from "react-infinite-scroll-component"
import { DocumentNode, useQuery } from "@apollo/client"
import { exceptionFromApolloError } from "../util/apolloError"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"

export const TASKS_CHUNK_SIZE = 50
export const scrollElementId = "scrollableDiv"

export interface TaskListTask {
  id: string
  name: string
  type: string
  taskPath: { id: string; name: string }[]
  deleted?: boolean
  changeType?: string
}

interface TasksDisplaySidebarProps {
  tasks: TaskListTask[]
  onTaskClicked?: (task: TaskListTask) => void
  fetchMoreData: () => void
  hasMoreData: boolean
  scrollElementId: string
  getTaskStatus?: (task: TaskListTask) => string | undefined
}

const TasksDisplaySidebarInner = (props: TasksDisplaySidebarProps) => {
  return (
    <ul className="list">
      <InfiniteScroll
        dataLength={props.tasks.length}
        next={props.fetchMoreData}
        hasMore={props.hasMoreData}
        loader={<LoadingSkeleton />}
        scrollableTarget={props.scrollElementId}
      >
        {props.tasks.map((task, index) => (
          <li className="list-item group border-bottom" key={index}>
            <Link
              to={`/tasks/${task.id}`}
              onClick={() => props.onTaskClicked && props.onTaskClicked(task)}
              title={renderTaskPath(task)}
            >
              <div className="group-items">
                {props.getTaskStatus && <TaskStatusIcon taskStatus={props.getTaskStatus(task)} />}
                <TaskTypeIcon type={task.type} />
                <span className="text two-lines-then-ellipsis">{task.name}</span>
              </div>
            </Link>
          </li>
        ))}
      </InfiniteScroll>
    </ul>
  )
}

const renderTaskPath = (task: TaskListTask) =>
  task.taskPath
    .slice()
    .reverse()
    .slice(1)
    .map((t: any) => t.name)
    .join("/")

export const LoadingSkeleton = () => (
  <>
    {[1, 2, 3].map((i) => (
      <div key={i} style={{ padding: "0.2rem 0.5rem 0.2rem 0.5rem" }} role="status">
        <Skeleton height={"2rem"} />
      </div>
    ))}
  </>
)

export const TasksDisplaySidebar = (props: {
  quadriProjectId: string
  scrollElementId: string
  include_deleted: boolean
  getTaskIdsSlice: (startIdx: number) => string[]
  nTasksTotal: number
  gqlQuery: DocumentNode
  getTaskStatus?: (task: TaskListTask) => string | undefined
}) => {
  const [tasks, setTasks] = useState<TaskListTask[]>([])

  const { getTaskIdsSlice, quadriProjectId, scrollElementId, include_deleted, nTasksTotal, gqlQuery } = props

  const { loading, error, data, fetchMore } = useQuery(gqlQuery, {
    variables: {
      quadriProjectId,
      taskIds: getTaskIdsSlice(0),
      include_deleted
    },
    errorPolicy: "all",
    skip: nTasksTotal === 0
  })
  if (error) throw exceptionFromApolloError(error)

  useEffect(() => {
    if (nTasksTotal === 0) {
      return setTasks([])
    }
    if (data) {
      const tasks = data?.getTasks || []
      setTasks(tasks)
    }
  }, [data, props, nTasksTotal])

  const nTasksCurrent = tasks.length

  // Followup queries
  const fetchMoreData = async () => {
    console.log("Fetching more data")
    fetchMore({
      variables: { quadriProjectId, taskIds: getTaskIdsSlice(nTasksCurrent) },
      updateQuery(previousQueryResult, options) {
        console.log("Updating query")
        const previousData = previousQueryResult.getTasks
        const newTasks = options.fetchMoreResult?.getTasks
        const newData = previousData.concat(newTasks)
        return { getTasks: newData }
      }
    })
  }

  const hasMoreData = tasks.length < nTasksTotal

  if (loading) {
    return <LoadingSkeleton />
  }

  return (
    <TasksDisplaySidebarInner
      tasks={tasks}
      fetchMoreData={fetchMoreData}
      hasMoreData={hasMoreData}
      scrollElementId={scrollElementId}
      getTaskStatus={props.getTaskStatus}
    />
  )
}
